"use client"

import { useClientTranslation } from "@/modules/App/components/i18n/i18n.client"
import { useMediaQuery } from "@/shared/hooks/use-media-query"
import { buildLegalURL } from "@/shared/utils/links"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Dialog,
  DialogClose,
  DialogOverlay,
  DialogPortal,
  DialogPrimitiveContent,
} from "@dalmatech/ui"
import { X } from "lucide-react"
import Image from "next/image"
import React from "react"
import { Drawer as DrawerPrimitive } from "vaul"

import * as styles from "./guide-overlay.styled"

type GuideOverlayProps = {
  isOpen: boolean
  onClose: () => void
  rcContractId?: string | null
}

type FaqItem = {
  key: string
  icon: React.ReactNode
  question: string
  answer: string
}

type DocumentItem = {
  key: string
  label: string
  href: string
}

const GuideContent = ({
  faqItems,
  modalityItems,
  documentItems,
}: {
  faqItems: FaqItem[]
  modalityItems: FaqItem[]
  documentItems: DocumentItem[]
}) => {
  const { t } = useClientTranslation("recap-page")
  return (
    <div className={styles.content}>
      {/* FAQ Section */}
      <div className={styles.sectionWrapper}>
        <p className={styles.sectionTitle}>{t("guide.faq.title")}</p>
        <Accordion type="single" collapsible className={styles.accordionWrapper}>
          {faqItems.map((item) => (
            <AccordionItem key={item.key} value={item.key} className={styles.accordionItem}>
              <AccordionTrigger Icon={item.icon} className={styles.accordionTrigger}>
                <span className={styles.accordionTriggerText}>{item.question}</span>
              </AccordionTrigger>
              <AccordionContent>
                <p className={styles.accordionBody}>{item.answer}</p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {/* Modalities Section */}
      <div className={styles.sectionWrapper}>
        <p className={styles.sectionTitle}>{t("guide.modalities.title")}</p>
        <Accordion type="single" collapsible className={styles.accordionWrapper}>
          {modalityItems.map((item) => (
            <AccordionItem key={item.key} value={item.key} className={styles.accordionItem}>
              <AccordionTrigger Icon={item.icon} className={styles.accordionTrigger}>
                <span className={styles.accordionTriggerText}>{item.question}</span>
              </AccordionTrigger>
              <AccordionContent>
                <p className={styles.accordionBody}>{item.answer}</p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {/* Documents Section */}
      <div className={styles.sectionWrapper}>
        <p className={styles.sectionTitle}>{t("guide.documents.title")}</p>
        <div className={styles.documentsCard}>
          {documentItems.map((doc) => (
            <div key={doc.key} className={styles.documentRow}>
              <Image src="/img/illus/file.svg" alt="" aria-hidden width={20} height={20} />
              <span className={styles.documentLabel}>{doc.label}</span>
              <a
                href={doc.href}
                target="_blank"
                rel="noreferrer"
                className={styles.documentViewButton}
              >
                {t("guide.documents.voir")}
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export const GuideOverlay = ({ isOpen, onClose, rcContractId }: GuideOverlayProps) => {
  const { t } = useClientTranslation("recap-page")
  const isDesktop = useMediaQuery("(min-width: 768px)")

  const icon = (src: string) => (
    <span className="flex shrink-0 items-center">
      <Image src={src} alt="" width={20} height={20} className="shrink-0" />
    </span>
  )

  const faqItems: FaqItem[] = [
    {
      key: "add-pet",
      icon: icon("/img/illus/bone-2025.svg"),
      question: t("guide.faq.items.add-pet.question"),
      answer: t("guide.faq.items.add-pet.answer"),
    },
    {
      key: "waiting-period",
      icon: icon("/img/illus/hourglass-new.svg"),
      question: t("guide.faq.items.waiting-period.question"),
      answer: t("guide.faq.items.waiting-period.answer"),
    },
    {
      key: "price-evolution",
      icon: icon("/img/illus/piggy-bank-blue.svg"),
      question: t("guide.faq.items.price-evolution.question"),
      answer: t("guide.faq.items.price-evolution.answer"),
    },
    {
      key: "exclusions",
      icon: icon("/img/illus/piggy-bank-blue.svg"),
      question: t("guide.faq.items.exclusions.question"),
      answer: t("guide.faq.items.exclusions.answer"),
    },
    {
      key: "commitment",
      icon: icon("/img/illus/calendar-new.svg"),
      question: t("guide.faq.items.commitment.question"),
      answer: t("guide.faq.items.commitment.answer"),
    },
  ]

  const modalityItems: FaqItem[] = [
    {
      key: "rate",
      icon: icon("/img/illus/money-bag.svg"),
      question: t("guide.modalities.items.rate.question"),
      answer: t("guide.modalities.items.rate.answer"),
    },
    {
      key: "annual-cap",
      icon: icon("/img/illus/wallet.svg"),
      question: t("guide.modalities.items.annual-cap.question"),
      answer: t("guide.modalities.items.annual-cap.answer"),
    },
    {
      key: "wellness",
      icon: icon("/img/illus/leafs.svg"),
      question: t("guide.modalities.items.wellness.question"),
      answer: t("guide.modalities.items.wellness.answer"),
    },
  ]

  const documentItems: DocumentItem[] = [
    {
      key: "fic",
      label: t("guide.documents.items.fic"),
      href: buildLegalURL(rcContractId, "FIC"),
    },
    {
      key: "cgu",
      label: t("guide.documents.items.guarantees-table"),
      href: buildLegalURL(rcContractId, "GUARANTEES_TABLE"),
    },
    {
      key: "dipa",
      label: t("guide.documents.items.dipa"),
      href: buildLegalURL(rcContractId, "DIPA"),
    },
  ]

  const guideContent = (
    <GuideContent faqItems={faqItems} modalityItems={modalityItems} documentItems={documentItems} />
  )

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogPortal>
          <DialogOverlay />
          <DialogPrimitiveContent className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <div className="size-10 shrink-0" />
              <p className={styles.dialogTitle}>{t("guide.title")}</p>
              <DialogClose>
                <button className={styles.dialogCloseButton}>
                  <X size={20} className="text-neutral-700" />
                </button>
              </DialogClose>
            </div>
            {guideContent}
          </DialogPrimitiveContent>
        </DialogPortal>
      </Dialog>
    )
  }

  return (
    <DrawerPrimitive.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay className="fixed inset-0 z-30 bg-neutral-600/60" />
        <DrawerPrimitive.Content className={styles.drawerContent}>
          <DrawerPrimitive.Title className="sr-only">{t("guide.title")}</DrawerPrimitive.Title>
          <div className={styles.drawerHeader}>
            <div className="size-9 shrink-0" />
            <p className={styles.drawerTitle}>{t("guide.title")}</p>
            <button onClick={onClose} className={styles.drawerCloseButton}>
              <X size={20} className="text-neutral-700" />
            </button>
          </div>
          {guideContent}
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  )
}
